/**
 * PASARSE, EN UN SOLO SITIO.
 *
 * Jesús, 13-08-2026: «Sí, se dice por cuánto: "te pasas 8 g de grasa"». El criterio vivía
 * repetido en siete sitios -- `getMealStatus` y `getDayStatus` en NutritionPage, las dos
 * funciones de MealCard, las barras de móvil y de escritorio de DayHeader y la barra de
 * DaySummary --, cada uno con su propio margen y su propia forma de decirlo. Cambiar la
 * regla significaba acertar en los siete. Ahora se cambia aquí.
 *
 * LA PROTEÍNA CUENTA COMO LOS OTROS DOS (5-09-2026, ficha 34 del parte de Gonzalo).
 *
 * Aquí estuvo, del 13-08 al 5-09, la excepción de Jesús («el rojo por arriba, solo en
 * hidratos y grasa; pasarse de proteína no»). Pero la regla que Francisco cerró el 3-09
 * (`lib/estadoDelMacro`) ya no la tenía: allí «sobran 12,8» sale en naranja sea el macro
 * que sea, y con la excepción viva aquí la MISMA comida decía «sobran 12,8» en naranja en
 * su fila de proteína y «faltan 72 de hidratos» a secas en la tarjeta plegada; y un día
 * pasado SOLO de proteína no sacaba el aviso «Este día no cuadra». Dos varas para el
 * mismo gramo. Manda la del 3-09: el margen de 4 de la casa, en los tres.
 *
 * Lo que NO cambia: quedarse corto. «Faltan 12 g» sigue avisando igual en los tres macros.
 */
import { num0, num1 } from './numeros';

// El margen de Calma (margenValido = 4): un macro está bien mientras no se pase de 4 g.
// Es el mismo número que ya usaban `getMealStatus` y las tarjetas de comida.
export const MARGEN = 4;

/**
 * El margen de un macro: LOS 4 G PLANOS, SIEMPRE (Francisco, 3-09-2026).
 *
 * Aquí vivía un margen proporcional -- una cuarta parte de lo pedido, mínimo 1,5 -- nacido
 * del intra: pide 9 g de proteína y con el margen de 4 un «5/9» salía como «comida
 * cuadrada» faltándole casi la mitad. Pero ese estrechado chocaba con la regla validada
 * del punto 11.1 de Jesús («de 1 a 4, falte o sobre, es válido y sale en verde. Igual en
 * toda la app, sin excepciones»): una grasa de 13 sobre 10 decía «sobran 3» en naranja
 * cuando el 11.1 la da por válida.
 *
 * Francisco eligió el 11.1 a la letra, SABIENDO que el caso del intra vuelve a caber en el
 * margen (5/9 saldrá «válido»): «1-4 plano en todo». Si algún día se revierte, es aquí y
 * en `NutritionChatbot.margen_de`, que es el mismo criterio en el backend.
 */
export const margenDe = () => MARGEN;

export const NOMBRE_MACRO = { P: 'proteína', H: 'hidratos', G: 'grasa' };

// LOS MACROS EN LOS QUE PASARSE ES UN FALLO: los tres. La proteína entró el 5-09 (ver
// arriba); si algún día vuelve a salir, es aquí y en `NutritionChatbot.margen_de`.
export const MACROS_QUE_SE_PASAN = ['P', 'H', 'G'];

// Con coma decimal, como el resto de los números que se le enseñan a una persona
// (Jesús, 15-08, fallo 43). El redondeo y el «sin decimales cuando son cero» no cambian.
export const fmtGramos = (x) => num1(x);

/**
 * ¿Este macro se ha pasado? La única definición de «pasarse» de la app.
 *
 * `esPeri`: en el intra y en el post la grasa no cuenta para nada, así que tampoco puede
 * pasarse (mismo criterio que ya tenía `getMealStatus`).
 */
export const seExcede = (key, servido, objetivo, { margen = MARGEN, esPeri = false } = {}) => {
    if (!MACROS_QUE_SE_PASAN.includes(key)) return false;
    if (esPeri && key === 'G') return false;
    // ESTRICTO, no >=: «de 1 a 4, falte o sobre, es válido» (punto 11.1) incluye el 4
    // clavado. Con >= aquí, un +4,0 exacto decía «válido» en la palabra y «te pasas» en el
    // aviso a la vez (alineado el 3-09, con la decisión del margen plano).
    return (servido || 0) - (objetivo || 0) > margen;
};

/** Los macros pasados, con sus gramos de más: [{ key: 'G', gramos: 8 }]. */
export const excesos = (servido = {}, objetivo = {}, opciones = {}) =>
    ['P', 'H', 'G']
        .filter(k => seExcede(k, servido[k], objetivo[k], opciones))
        .map(k => ({ key: k, gramos: (servido[k] || 0) - (objetivo[k] || 0) }));

/**
 * «8 g de grasa», o «12 g de hidratos y 8 g de grasa». Sin el «Te pasas» delante, para
 * que cada sitio lo monte con su tipografía; el texto es el mismo que ya usa el chat
 * (`ChatMealSummary`), y por eso la app dice lo mismo en todas partes.
 */
export const textoExceso = (servido, objetivo, opciones) => {
    const lista = excesos(servido, objetivo, opciones)
        .map(({ key, gramos }) => `${fmtGramos(gramos)} g de ${NOMBRE_MACRO[key]}`);
    if (!lista.length) return '';
    return lista.length === 1 ? lista[0] : `${lista.slice(0, -1).join(', ')} y ${lista[lista.length - 1]}`;
};

/** «Te pasas 8 g de grasa» ya montado, para los sitios que enseñan una frase suelta. */
export const fraseExceso = (servido, objetivo, opciones) => {
    const t = textoExceso(servido, objetivo, opciones);
    return t ? `Te pasas ${t}` : '';
};

// ── LO QUE FALTA O SOBRA, DICHO CON PALABRAS ────────────────────────────────────────────
//
// Vivía dentro de `pages/NutritionPage`, y lo usan los dos «Cuadrar» (el de una comida y el
// del día entero) y el cartel de arriba de Nutrición. Baja aquí, al lado del resto del
// criterio de «pasarse», para poder probarlo: es la frase que más veces se ha corregido de
// toda la pantalla y no había forma de vigilarla.
//
// EL LISTÓN SON 4 g, los mismos de `MARGEN`. Por debajo no hay nada que contarle: es el
// ruido de redondear a cantidades que se puedan pesar, y llamarlo «no cuadra» sería no
// dejarle cuadrar nunca.
//
// ARRIBA REDONDOS, ABAJO CON DECIMAL (fichas 28 y 34 del parte de Gonzalo, 4-09-2026).
//
// Su ficha 34: el cartel de arriba de Nutrición decía «faltan 53,2 g de proteína, faltan
// 46,3 g de hidratos y sobran 7,6 g de grasa». Y la 28 pone la regla entera: «decimales
// ABAJO sí, ARRIBA no -- la comida y los ingredientes con decimales; los "faltan/sobran"
// de arriba, redondeados».
//
// No es cosmética: el cartel de arriba habla del DÍA ENTERO, cientos de gramos, y ahí la
// décima no cambia ninguna decisión, sólo alarga una frase que se lee de un vistazo. Dentro
// de la comida sí importa, porque se mueven cantidades pequeñas y la décima es la
// diferencia entre cuadrar y no. La FRASE es la misma en los dos sitios a propósito (los
// dos «Cuadrar» y el cartel tienen que contarlo igual): lo único que cambia es con qué se
// escribe el número.
export const MARGEN_QUE_SE_DICE = MARGEN;

/**
 * @param desfase  {P,H,G} en gramos: negativo = falta, positivo = sobra.
 * @param n        cómo se escribe el número: `num1` (con décima, dentro de la comida) o
 *                 `num0` (redondo, el cartel de arriba).
 * @returns null si cuadra dentro del margen, o la frase («faltan 21 g de proteína y ...»).
 */
export const desajusteEnPalabras = (desfase, n = num1) => {
    if (!desfase) return null;
    const fuera = ['P', 'H', 'G'].filter(m => Math.abs(desfase[m] || 0) > MARGEN_QUE_SE_DICE);
    if (!fuera.length) return null;
    const trozos = fuera.map(m => {
        const v = desfase[m];
        return `${v > 0 ? 'sobran' : 'faltan'} ${n(Math.abs(v))} g de ${NOMBRE_MACRO[m]}`;
    });
    // Con los tres macros fuera, unirlos todos con «y» daba «faltan A y faltan B y sobran
    // C». Coma entre los primeros y «y» solo antes del último, que es como se escribe.
    if (trozos.length === 1) return trozos[0];
    return `${trozos.slice(0, -1).join(', ')} y ${trozos[trozos.length - 1]}`;
};

/** La de arriba, redonda, para no tener que acordarse de pasar `num0`. */
export const desajusteEnPalabrasRedondo = (desfase) => desajusteEnPalabras(desfase, num0);
